import { useEffect, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import VerloInsightPanel from '../../../components/reports/VerloInsightPanel';
import { reportsIntelApi } from '@verlo/shared/services/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@verlo/shared/components/ui/sheet';

const RANGES = [
  { value: 'today',    label: 'Today' },
  { value: 'wtd',      label: 'Week to date' },
  { value: 'mtd',      label: 'Month to date' },
  { value: 'last_30',  label: 'Last 30 days' },
  { value: 'last_90',  label: 'Last 90 days' },
];

const METRICS = [
  { value: 'revenue', label: 'Revenue',         fmt: 'currency' },
  { value: 'covers',  label: 'Covers',          fmt: 'int' },
  { value: 'aov',     label: 'Avg order value', fmt: 'currency' },
  { value: 'upsell',  label: 'Upsell rate',     fmt: 'percent' },
  { value: 'premium', label: 'Premium mix',     fmt: 'percent' },
];

const fmtVal = (kpi, v) => {
  if (v === null || v === undefined) return '—';
  if (kpi === 'currency') return `£${Math.round(v).toLocaleString('en-GB')}`;
  if (kpi === 'percent') return `${v}%`;
  return Math.round(v).toLocaleString('en-GB');
};

const initials = (name) => {
  if (!name) return '?';
  return name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
};

const StaffPerformanceIntelligencePage = () => {
  const [range, setRange] = useState('last_30');
  const [metric, setMetric] = useState('revenue');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reportsIntelApi.getStaffLeaderboard(range, metric, undefined, 20)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => !cancelled && toast.error('Could not load staff leaderboard'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [range, metric]);

  const openDetail = async (staff) => {
    setSelected(staff);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await reportsIntelApi.getStaffDetail(staff.id, range);
      setDetail(r.data);
    } catch {
      toast.error('Could not load staff detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const metricMeta = METRICS.find((m) => m.value === metric) || METRICS[0];
  const metricKey = metric === 'upsell' ? 'upsell_rate' : metric === 'premium' ? 'premium_pct' : metric;

  return (
    <ReportsLayout>
      <div data-testid="staff-intelligence-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              Who sold what: takings, spend per order and add-ons, person by person. Pick someone to see more.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-[180px] h-9 text-[12px]" data-testid="staff-metric-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-[160px] h-9 text-[12px]" data-testid="staff-range-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Verlo Insight */}
        <div className="mb-5">
          <VerloInsightPanel
            topic="staff"
            range={range}
            label="The behaviour worth coaching this week"
            hint="A short read on the single performance pattern most worth replicating across your team."
          />
        </div>

        {/* The top three, in order, the same as any other figures in the back office (Back
            office sweep, Oct 2026: no dark winner's card or crown). */}
        {data && data.rows.length >= 3 && (
          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="staff-podium">
            {data.rows.slice(0, 3).map((s) => (
              <button
                type="button"
                onClick={() => openDetail(s)}
                key={s.id}
                className="rounded-lg border border-hairline bg-white p-4 text-left hover:bg-hover-tint"
                data-testid={`podium-${s.id}`}
              >
                <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">#{s.rank}</div>
                <div className="mt-1 text-[14px] font-semibold text-ink">{s.name}</div>
                <div className="mt-1 font-mono text-[24px] leading-none tabular-nums text-ink">{fmtVal(metricMeta.fmt, s[metricKey])}</div>
                <div className="mt-1.5 text-[12.5px] text-ink-faint">{s.orders} orders · {s.covers} covers</div>
              </button>
            ))}
          </div>
        )}

        {/* Full leaderboard */}
        <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="staff-leaderboard-table">
          <div className="px-5 py-3 border-b border-hairline flex items-center justify-between">
            <div>
              <h3 className="font-heading text-[15px] font-extrabold text-ink">Everyone</h3>
              <p className="text-[12.5px] text-ink-soft mt-0.5">Pick someone to see their week</p>
            </div>
            <span className="text-[12.5px] text-ink-soft">{data?.rows.length || 0} ranked by {metricMeta.label.toLowerCase()}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-ink-soft">
                  <th className="text-left px-4 py-2.5 font-semibold w-[60px]">#</th>
                  <th className="text-left px-2 py-2.5 font-semibold">Name</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Revenue</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Orders</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Covers</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Per order</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Upsell</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Premium</th>
                  <th className="px-3 py-2.5 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-ink-faint">Loading…</td></tr>
                )}
                {!loading && data?.rows.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-ink-faint">No staff activity in this window.</td></tr>
                )}
                {!loading && data?.rows.map((s) => (
                  <tr key={s.id} onClick={() => openDetail(s)} className="border-t border-hairline hover:bg-hover-tint cursor-pointer group" data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-mono tabular-nums text-[12.5px] text-ink-faint">{s.rank}</span>
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-paper text-ink-soft flex items-center justify-center text-[12.5px] font-semibold">
                          {initials(s.name)}
                        </div>
                        <span className="font-medium text-ink">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono-display font-semibold tabular-nums">£{Math.round(s.revenue).toLocaleString('en-GB')}</td>
                    <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{s.orders}</td>
                    <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{s.covers}</td>
                    <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">£{s.aov}</td>
                    <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{s.upsell_rate}%</td>
                    <td className="px-3 py-2.5 text-right font-mono-display tabular-nums text-ink-soft">{s.premium_pct}%</td>
                    <td className="px-3 py-2.5 text-ink-faint group-hover:text-ink-soft transition-colors">
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail sheet */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="staff-detail-sheet">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-paper text-ink-soft flex items-center justify-center text-sm font-semibold">
                  {initials(selected?.name)}
                </div>
                <div>
                  <div className="text-base text-ink">{selected?.name}</div>
                  <div className="text-[12.5px] text-ink-soft font-normal">Rank #{selected?.rank} · {selected?.orders} orders</div>
                </div>
              </SheetTitle>
            </SheetHeader>

            <div className="mt-6 space-y-5">
              {detailLoading && <div className="text-ink-faint text-sm">Loading…</div>}
              {detail && !detail.found && <div className="text-ink-soft text-sm">No detailed activity in this window.</div>}
              {detail && detail.found && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-surface-2 border border-hairline rounded-lg p-3">
                      <div className="text-[11px] uppercase tracking-wider text-ink-soft font-semibold">Revenue</div>
                      <div className="font-mono-display text-2xl font-medium tabular-nums mt-1">£{Math.round(detail.total_revenue).toLocaleString('en-GB')}</div>
                    </div>
                    <div className="bg-surface-2 border border-hairline rounded-lg p-3">
                      <div className="text-[11px] uppercase tracking-wider text-ink-soft font-semibold">Orders</div>
                      <div className="font-mono-display text-2xl font-medium tabular-nums mt-1">{detail.order_count}</div>
                    </div>
                  </div>

                  {detail.top_categories.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Top categories</h4>
                      <div className="space-y-1.5">
                        {detail.top_categories.map((c) => {
                          const max = Math.max(...detail.top_categories.map((x) => x.revenue));
                          const w = max ? (c.revenue / max) * 100 : 0;
                          return (
                            <div key={c.category} className="flex items-center gap-3 text-[12px]">
                              <span className="w-24 text-ink-soft truncate">{c.category}</span>
                              <div className="flex-1 h-2 bg-paper rounded-full overflow-hidden">
                                <div className="h-full bg-ink-soft rounded-full" style={{ width: `${w}%` }} />
                              </div>
                              <span className="font-mono-display tabular-nums text-ink w-16 text-right">£{Math.round(c.revenue).toLocaleString('en-GB')}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {detail.top_products.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Top products</h4>
                      <ul className="divide-y divide-hairline border border-hairline rounded-lg">
                        {detail.top_products.map((p, i) => (
                          <li key={i} className="flex items-center justify-between px-3 py-2 text-[12px]">
                            <span className="text-ink font-medium">{p.name}</span>
                            <span className="flex items-center gap-3">
                              <span className="text-ink-soft">{p.qty}u</span>
                              <span className="font-mono-display tabular-nums text-ink w-16 text-right">£{Math.round(p.revenue).toLocaleString('en-GB')}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {detail.hourly_revenue.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-2">Hour-of-day revenue</h4>
                      <div className="flex items-end gap-0.5 h-20 bg-surface-2 border border-hairline rounded-lg p-2">
                        {Array.from({ length: 24 }).map((_, h) => {
                          const row = detail.hourly_revenue.find((x) => x.hour === h);
                          const v = row?.revenue || 0;
                          const max = Math.max(...detail.hourly_revenue.map((x) => x.revenue));
                          const heightPct = max ? (v / max) * 100 : 0;
                          return (
                            <div key={h} className="flex-1 flex flex-col items-center justify-end" title={`${h}:00 · £${Math.round(v)}`}>
                              <div className="w-full bg-ink-soft rounded-md" style={{ height: `${heightPct}%`, minHeight: v > 0 ? '2px' : '0' }} />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-[12.5px] text-ink-faint mt-1 px-1">
                        <span>0</span><span>6</span><span>12</span><span>18</span><span>24</span>
                      </div>
                    </div>
                  )}
                </>
              )}
              <Button variant="outline" onClick={() => setSelected(null)} className="w-full" data-testid="staff-detail-close">
                <X size={13} className="mr-1" />Close
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </ReportsLayout>
  );
};

export default StaffPerformanceIntelligencePage;
